import React, { FormEvent, useState } from 'react';
import { ChevronLeft, Send } from 'lucide-react';
const CHAT_AVATAR = 'https://cdn.magicpatterns.com/uploads/bWheurp3vUHCL4f75s5nsV/image.png';
const initialSessions = [{
  id: 'session-1',
  summary: 'How can I make learning time feel more playful at home?',
  date: 'Today, 10:46 AM'
}, {
  id: 'session-2',
  summary: 'Ideas for helping children talk about big feelings.',
  date: '22 Jul 2025, 2:25 PM'
}, {
  id: 'session-3',
  summary: 'Simple activities for a calm bedtime routine.',
  date: '22 Jul 2025, 1:32 PM'
}];
type ChatMessage = {
  id: string;
  role: 'assistant' | 'user';
  text: string;
};
type ChatScreenProps = {
  onBack: () => void;
};
export function ChatScreen({
  onBack
}: ChatScreenProps) {
  const [sessions, setSessions] = useState(initialSessions);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [message, setMessage] = useState('');
  const [chatOpen, setChatOpen] = useState(false);
  const startNewChat = () => {
    setMessages([{
      id: 'welcome',
      role: 'assistant',
      text: 'Hi! I’m here to help you explore playful ways to learn and grow together.'
    }]);
    setMessage('');
    setChatOpen(true);
  };
  const sendMessage = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedMessage = message.trim();
    if (!trimmedMessage) return;
    setMessages(current => [...current, {
      id: `user-${Date.now()}`,
      role: 'user',
      text: trimmedMessage
    }, {
      id: `assistant-${Date.now()}`,
      role: 'assistant',
      text: 'That sounds important. Would you like a few gentle activity ideas to try together?'
    }]);
    setSessions(current => [{
      id: `session-${Date.now()}`,
      summary: trimmedMessage,
      date: 'Just now'
    }, ...current]);
    setMessage('');
  };
  if (chatOpen) {
    return <section className="flex h-full flex-col px-4 pb-4 pt-2 text-white" data-id="element-205" data-mp="qjk9q4lx2raz">
        <div className="flex items-center justify-between" data-id="element-206" data-mp="qlod8lfwloaq">
          <button onClick={() => setChatOpen(false)} className="flex items-center rounded-full bg-[#5b5268]/85 px-2 py-1 text-[10px]" data-id="element-207" data-mp="lm1xo5clrvyr">
            <ChevronLeft className="h-3.5 w-3.5" data-id="element-208" data-mp="bzhjq7y493pg" />
            Back
          </button>
          <div className="flex items-center gap-1.5 text-[10px] font-bold" data-id="element-209" data-mp="gxizaiwc2kw6"><img src={CHAT_AVATAR} alt="Rangeet AI" className="h-6 w-6 rounded-full object-cover" data-id="element-210" data-mp="200h5ze5v5xp" />Rangeet AI</div>
        </div>

        <div className="mt-4 flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto" data-id="element-211" data-mp="6yacqb3ss8ju">
          {messages.map(chatMessage => <div key={chatMessage.id} className={`max-w-[84%] rounded-xl px-3 py-2 text-[10px] leading-relaxed ${chatMessage.role === 'user' ? 'ml-auto bg-white text-[#413946]' : 'bg-[#5b5268]/90 text-white'}`} data-id="element-212" data-mp="uppgm8b82xx7">{chatMessage.text}</div>)}
        </div>

        <form onSubmit={sendMessage} className="mt-3 flex items-center gap-2" data-id="element-213" data-mp="pw4goaq2jtvd">
          <label className="sr-only" htmlFor="ai-message" data-id="element-214" data-mp="1dmqmdvclp227">Message Rangeet AI</label>
          <input id="ai-message" value={message} onChange={event => setMessage(event.target.value)} placeholder="Write a message..." className="h-10 min-w-0 flex-1 rounded-md bg-[#5b5268]/90 px-3 text-[10px] text-white placeholder:text-white/50 outline-none focus:ring-2 focus:ring-white/70" data-id="element-215" data-mp="1id71djw2kvqo" />
          <button type="submit" aria-label="Send message" className="grid h-10 w-10 place-items-center rounded-md bg-white text-[#413946] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70" data-id="element-216" data-mp="3ctfkgx8se70t"><Send className="h-4 w-4" data-id="element-217" data-mp="2k4qh0rk9dplw" /></button>
        </form>
      </section>;
  }
  return <section className="h-full overflow-y-auto px-4 pb-4 pt-2 text-white" data-id="element-218" data-mp="2f5na11kpx9ba">
      <div className="flex items-center justify-between" data-id="element-219" data-mp="2f7hygpv77jk4">
        <button onClick={onBack} aria-label="Back to home" className="flex items-center rounded-full bg-[#5b5268]/85 px-2 py-1 text-[10px]" data-id="element-220" data-mp="2t5ojh5f1tp2d">
          <ChevronLeft className="h-3.5 w-3.5" data-id="element-221" data-mp="2p5xn8m6r6gqg" />
          Back
        </button>
        <img src={CHAT_AVATAR} alt="Rangeet AI" className="h-8 w-8 rounded-full border border-white/60 object-cover" data-id="element-222" data-mp="8dmfnva66sn4" />
      </div>

      <button onClick={startNewChat} className="mt-8 h-11 w-full rounded-md bg-[#283a7a] text-sm font-bold shadow-sm active:scale-[.98]" data-id="element-223" data-mp="4fyguh5cwfpy">START NEW CHAT</button>
      <h1 className="mt-7 text-center text-base font-bold" data-id="element-224" data-mp="13q5hoj17j958">Session history</h1>
      <div className="mt-3 space-y-3" data-id="element-225" data-mp="18o79bscp8gsb">
        {sessions.map(session => <button key={session.id} onClick={startNewChat} className="w-full rounded-xl bg-[#5b5268]/75 px-4 py-4 text-left shadow-sm transition hover:bg-[#5b5268]/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70" data-id="element-226" data-mp="1dnjywkuyg1yl"><p className="text-[11px] font-semibold leading-relaxed" data-id="element-227" data-mp="1mngezy3a3si1">{session.summary}</p><p className="mt-2 text-[9px] text-white/80" data-id="element-228" data-mp="1rf89gjpk2idg">{session.date}</p></button>)}
      </div>
    </section>;
}